import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  UserCircleIcon,
  CogIcon,
  ArrowLeftIcon,
} from "@heroicons/react/24/outline";
import { apiService } from "../services/apiService";
import { useError } from "../context/ErrorContext";

const Profile = () => {
  const navigate = useNavigate();
  const { showError } = useError();
  const [profile, setProfile] = useState(null);
  const [usage, setUsage] = useState(null);
  const [remainingLimits, setRemainingLimits] = useState(null);
  const [totalLimits, setTotalLimits] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    name: "",
    profilePicture: "",
    preferences: {
      theme: "dark",
      emailNotifications: true,
    },
  });

  useEffect(() => {
    loadProfile();
    const interval = setInterval(loadProfile, 10000);
    return () => clearInterval(interval);
  }, []);

  const loadProfile = async () => {
    try {
      console.log("Fetching profile data...");
      const data = await apiService.get("/api/profile");
      console.log("Received profile data:", data);
      setProfile(data.profile);
      setUsage(data.usage);
      setRemainingLimits(data.remainingLimits);
      setTotalLimits(data.totalLimits);
      setEditForm({
        name: data.profile.name,
        profilePicture: data.profile.profilePicture,
        preferences: data.profile.preferences,
      });
    } catch (error) {
      showError(error.message);
    }
  };

  const handleUpdate = async () => {
    try {
      const updatedProfile = await apiService.patch("/api/profile", editForm);
      setProfile(updatedProfile);
      setIsEditing(false);
      showError("Profile updated successfully!", "success");
    } catch (error) {
      showError(error.message);
    }
  };

  const handleNavigateBack = () => {
    navigate("/dashboard");
  };

  const formatRemainingCount = (count, type, plan) => {
    if (plan === "professional" && (type === "chat" || type === "summary")) {
      return "∞";
    }
    return count;
  };

  const getProgressBarStyle = (type, plan, usage, totalLimit) => {
    if (plan === "professional" && (type === "chat" || type === "summary")) {
      return {
        background: "linear-gradient(to right, #3b82f6, #8b5cf6, #ec4899)",
        width: "100%",
      };
    }

    // Get the correct limit based on plan and type
    let limit;
    if (plan === "free") {
      switch (type) {
        case "chat":
          limit = 50;
          break;
        case "summary":
          limit = 15;
          break;
        case "notes":
          limit = 10;
          break;
        case "test":
          limit = 5;
          break;
        default:
          limit = totalLimit;
      }
    } else if (plan === "student") {
      switch (type) {
        case "chat":
          limit = 1200;
          break;
        case "summary":
          limit = 200;
          break;
        case "notes":
          limit = 120;
          break;
        case "test":
          limit = 90;
          break;
        default:
          limit = totalLimit;
      }
    } else {
      limit = totalLimit;
    }

    return {
      background: "#3b82f6", // blue-500
      width: `${Math.min(100, ((usage || 0) / limit) * 100)}%`,
    };
  };

  return (
    <div className="min-h-screen bg-dark-bg text-gray-200 p-8">
      <div className="fixed top-0 left-0 right-0 h-14 bg-dark-bg border-b border-gray-800/40 flex items-center px-4 z-10">
        <button
          onClick={handleNavigateBack}
          className="text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5" />
        </button>
      </div>

      <div className="max-w-4xl mx-auto pt-14">
        <h1 className="text-3xl font-bold mb-8">Profile</h1>

        {/* Profile Info */}
        <div className="bg-dark-card rounded-lg p-6 mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6">
            <div className="flex items-center mb-4 sm:mb-0">
              <div className="relative">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 p-0.5">
                  <div className="w-full h-full rounded-full bg-dark-bg p-1">
                    {profile?.profilePicture ? (
                      <img
                        src={profile.profilePicture}
                        alt={profile.name}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <UserCircleIcon className="w-full h-full text-gray-400" />
                    )}
                  </div>
                </div>
                {!isEditing && (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="absolute -bottom-2 -right-2 p-2 bg-blue-500 rounded-full hover:bg-blue-600 transition-colors"
                  >
                    <CogIcon className="w-4 h-4 text-white" />
                  </button>
                )}
              </div>
              <div className="ml-4">
                <h2 className="text-xl sm:text-2xl font-semibold text-white">
                  {profile?.name || "Loading..."}
                </h2>
                <p className="text-gray-400 text-sm sm:text-base">
                  {profile?.email || "Loading..."}
                </p>
              </div>
            </div>
          </div>

          {isEditing && (
            <div className="space-y-6 mt-6 border-t border-gray-800 pt-6">
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Display Name
                  </label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) =>
                      setEditForm({ ...editForm, name: e.target.value })
                    }
                    className="w-full bg-dark-bg text-white px-4 py-2 rounded-lg border border-gray-800 focus:border-blue-500 focus:outline-none transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Profile Picture URL
                  </label>
                  <input
                    type="text"
                    value={editForm.profilePicture}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        profilePicture: e.target.value,
                      })
                    }
                    className="w-full bg-dark-bg text-white px-4 py-2 rounded-lg border border-gray-800 focus:border-blue-500 focus:outline-none transition-colors"
                  />
                </div>

                <div>
                  <label className="flex items-center space-x-3 cursor-pointer group">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={editForm.preferences.emailNotifications}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            preferences: {
                              ...editForm.preferences,
                              emailNotifications: e.target.checked,
                            },
                          })
                        }
                        className="sr-only"
                      />
                      <div
                        className={`w-10 h-6 rounded-full transition-colors ${
                          editForm.preferences.emailNotifications
                            ? "bg-blue-500"
                            : "bg-gray-700"
                        }`}
                      >
                        <div
                          className={`w-4 h-4 rounded-full bg-white transition-transform transform ${
                            editForm.preferences.emailNotifications
                              ? "translate-x-5"
                              : "translate-x-1"
                          } mt-1`}
                        />
                      </div>
                    </div>
                    <span className="text-gray-300 group-hover:text-white transition-colors">
                      Email Notifications
                    </span>
                  </label>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <button
                  onClick={handleUpdate}
                  className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  Save Changes
                </button>
                <button
                  onClick={() => setIsEditing(false)}
                  className="px-6 py-2 bg-dark-bg text-gray-400 rounded-lg hover:bg-dark-hover hover:text-white transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Usage Stats */}
        <div className="bg-dark-card rounded-lg p-6">
          <h3 className="text-xl font-semibold mb-6 text-white">
            AI Usage This Month
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Chat Requests */}
            <div className="p-4 bg-dark-bg rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-medium">Chat Requests</h4>
                <span className="text-blue-400">
                  {formatRemainingCount(
                    remainingLimits?.chat || 0,
                    "chat",
                    profile?.subscription?.plan
                  )}{" "}
                  remaining
                </span>
              </div>
              <div className="w-full bg-dark-hover rounded-full h-2 mb-1">
                <div
                  className="h-2 rounded-full transition-all duration-300"
                  style={getProgressBarStyle(
                    "chat",
                    profile?.subscription?.plan,
                    usage?.chat,
                    profile?.subscription?.plan === "free"
                      ? 50
                      : totalLimits?.chat
                  )}
                />
              </div>
              <div className="text-xs text-gray-400">
                {usage?.chat || 0}/
                {profile?.subscription?.plan === "professional"
                  ? "∞"
                  : totalLimits?.chat}{" "}
                used
              </div>
            </div>

            {/* Summary Requests */}
            <div className="p-4 bg-dark-bg rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-medium">Summary Requests</h4>
                <span className="text-blue-400">
                  {formatRemainingCount(
                    remainingLimits?.summary || 0,
                    "summary",
                    profile?.subscription?.plan
                  )}{" "}
                  remaining
                </span>
              </div>
              <div className="w-full bg-dark-hover rounded-full h-2 mb-1">
                <div
                  className="h-2 rounded-full transition-all duration-300"
                  style={getProgressBarStyle(
                    "summary",
                    profile?.subscription?.plan,
                    usage?.summary,
                    profile?.subscription?.plan === "free"
                      ? 15
                      : totalLimits?.summary
                  )}
                />
              </div>
              <div className="text-xs text-gray-400">
                {usage?.summary || 0}/
                {profile?.subscription?.plan === "professional"
                  ? "∞"
                  : totalLimits?.summary}{" "}
                used
              </div>
            </div>

            {/* Detailed Notes */}
            <div className="p-4 bg-dark-bg rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-medium">Detailed Notes</h4>
                <span className="text-blue-400">
                  {remainingLimits?.notes || 0} remaining
                </span>
              </div>
              <div className="w-full bg-dark-hover rounded-full h-2 mb-1">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${Math.min(
                      100,
                      ((usage?.notes || 0) /
                        (profile?.subscription?.plan === "free"
                          ? 10
                          : parseInt(totalLimits?.notes || 1))) *
                        100
                    )}%`,
                  }}
                />
              </div>
              <div className="text-xs text-gray-400">
                {usage?.notes || 0}/
                {profile?.subscription?.plan === "free"
                  ? "10"
                  : parseInt(totalLimits?.notes || 0)}{" "}
                used
              </div>
            </div>

            {/* Test Generation */}
            <div className="p-4 bg-dark-bg rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-medium">Test Generation</h4>
                <span className="text-blue-400">
                  {remainingLimits?.test || 0} remaining
                </span>
              </div>
              <div className="w-full bg-dark-hover rounded-full h-2 mb-1">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${Math.min(
                      100,
                      ((usage?.test || 0) /
                        (profile?.subscription?.plan === "free"
                          ? 5
                          : parseInt(totalLimits?.test || 1))) *
                        100
                    )}%`,
                  }}
                />
              </div>
              <div className="text-xs text-gray-400">
                {usage?.test || 0}/
                {profile?.subscription?.plan === "free"
                  ? "5"
                  : parseInt(totalLimits?.test || 0)}{" "}
                used
              </div>
            </div>
          </div>
        </div>

        {/* Subscription Section */}
        <div className="bg-dark-card rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-semibold text-white">Subscription</h3>
              <p className="text-gray-400">
                Current Plan: {profile?.subscription?.plan || "Free"}
              </p>
            </div>
            <button
              onClick={() => navigate("/settings/subscription")}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              Manage Subscription
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
